// Client-side PostHog singleton — EU Frankfurt, autocapture disabled
// Consent-gated: PostHog does NOT initialize until user opts in to Analytics (D-16)
import posthog from "posthog-js";
import type { AnalyticsEvent, EventProperties } from "~/lib/analytics-events";
import { getCookieConsent } from "~/lib/cookie-consent-state";
import { scrubEventProperties } from "~/lib/security/observability-policy";

let initialized = false;

/**
 * Initialize PostHog on the client.
 * Only runs in the browser, only when VITE_POSTHOG_KEY is set,
 * and only when the user has opted in to the Analytics cookie category (D-16).
 * Autocapture is disabled — all events are explicit via Plan 02.
 */
export function initPosthog(): void {
  if (typeof window === "undefined") return;
  if (initialized) return;

  // D-16: Do NOT load PostHog until user opts in to analytics category
  const consent = getCookieConsent();
  if (!consent?.analytics) return;

  const key = import.meta.env.VITE_POSTHOG_KEY;
  // Use reverse proxy (/ingest) to bypass ad blockers; fall back to direct EU endpoint
  const host = import.meta.env.VITE_POSTHOG_HOST || "/ingest";

  if (!key) {
    if (import.meta.env.DEV) {
      console.log("[posthog:client] No VITE_POSTHOG_KEY — skipping init");
    }
    return;
  }

  posthog.init(key, {
    api_host: host,
    ui_host: "https://eu.posthog.com",
    autocapture: false,
    capture_pageview: false,
    // Automatic lifecycle events bypass the checked-in property policy.
    capture_pageleave: false,
    persistence: "localStorage+cookie",
    opt_out_capturing_by_default: true,
  });

  initialized = true;
}

/**
 * Get the PostHog client instance.
 * Returns the posthog-js instance for explicit event capture and consent management.
 */
export function getPosthogClient(): typeof posthog {
  return posthog;
}

/**
 * Type-safe client-side event tracking. Enforces the curated 15-event schema.
 *
 * Usage:
 *   import { trackEvent } from '~/lib/posthog-client';
 *   trackEvent('resume_tailored', { job_title: 'Engineer', match_score: 87, credits_used: 1 });
 *
 * Compile-time error if you pass an event not in ANALYTICS_EVENTS or wrong properties.
 * Safe to call during SSR (no-ops when window is undefined).
 */
export function trackEvent<E extends AnalyticsEvent>(
  event: E,
  properties: E extends keyof EventProperties ? EventProperties[E] : never,
): void {
  if (typeof window === "undefined" || !initialized) return;
  if (!getCookieConsent()?.analytics) return;

  const safe = scrubEventProperties(event, properties);
  if (!safe) return;
  posthog.capture(safe.event, safe.properties);
}
